import { convertToPlainText } from './richContent/convertToPlainText';

import type { FieldInfoPanel } from '../types/fieldInfoPanel';

/**
 * Field-agnostic helpers for text-assist example insert (not ⓘ help).
 *
 * To add the same pattern to another field:
 * 1. Add copy as a `FieldInfoPanel` with `examples` (no angle brackets).
 * 2. Render `FormFieldTrailingIcon` with the Figma sparkle-pencil (`WandSparkles` stub until the asset lands).
 * 3. RHF fields: call `useInsertExampleAtEnd` and pass the chosen example text
 *    into `insert`. Deliverables comments are not RHF — `appendExampleText` +
 *    `onAdd` / `onEdit` instead. Do not call the hook there.
 * 4. Open `FieldTextAssist`; pass `alreadyInserted` / `canInsertExample` from
 *    live values (`getValues()` / the current comment), not a watched snapshot
 *    (watch can lag the insert path).
 * 5. RHF fields: pass `onDismiss={focusAfterDismiss}`. Focus runs only after a
 *    successful insert, once the overlay fade has finished.
 * 6. Trigger `accessibilityHint` must not dump example bodies (`includeExamples:
 *    false` and `includeIntro: false`, or a short dedicated hint).
 *
 * Append is strictly additive (never clobbers). Insertion is all-or-nothing
 * against `maxLength`. Non-empty content keeps one blank separator before the
 * example (`\n\n` plain, one empty paragraph rich); a blank field takes the
 * example alone. Same example text cannot be inserted while it remains in the
 * field (`exampleAlreadyInserted`), matched on projected text so a rich editor
 * that re-tags the markup still blocks a duplicate.
 * Consumers: Project Description (RHF hook); Deliverables comments (onAdd/onEdit).
 */

export function fieldInfoPanelA11y(
  panel: FieldInfoPanel,
  options?: { includeExamples?: boolean; includeIntro?: boolean },
): string {
  const intro =
    options?.includeIntro === false ? undefined : panel.examplesIntro;
  const base = [`${panel.title}.`, panel.bullets.join(' '), intro]
    .filter((bit): bit is string => bit != null && bit.length > 0)
    .join(' ');
  if (options?.includeExamples === false) {
    return base;
  }
  const exampleBits = [
    panel.example,
    ...(panel.examples ?? []).map(item => item.text),
  ].filter((bit): bit is string => bit != null && bit.length > 0);
  if (exampleBits.length === 0) {
    return base;
  }
  return `${base} ${exampleBits.join(' ')}`;
}

function withNormalizedNewlines(value: string): string {
  return value.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
}

function escapeHtmlChars(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

/** Leading unordered or ordered markers used in example copy. */
const LIST_LINE = /^([-*•]|\d+\.)\s+(.*)$/;

type ListLine = {
  body: string;
  tag: 'ol' | 'ul';
};

function listLine(line: string): ListLine | null {
  const match = LIST_LINE.exec(line);
  if (!match) {
    return null;
  }
  return {
    body: match[2] ?? '',
    tag: /^\d+\.$/.test(match[1] ?? '') ? 'ol' : 'ul',
  };
}

function blockToRichHtml(block: string): string {
  const lines = block.split('\n');
  const parts: string[] = [];
  let index = 0;
  while (index < lines.length) {
    const firstListLine = listLine(lines[index] ?? '');
    if (firstListLine != null) {
      const items: string[] = [firstListLine.body];
      index += 1;
      while (index < lines.length) {
        const next = listLine(lines[index] ?? '');
        if (next == null || next.tag !== firstListLine.tag) {
          break;
        }
        items.push(next.body);
        index += 1;
      }
      // Bare `<li>` (no wrapping `<p>`) is what ProseMirror re-serializes to
      // and what `convertToPlainText` projects as one line per item.
      parts.push(
        `<${firstListLine.tag}>${items
          .map(item => `<li>${escapeHtmlChars(item)}</li>`)
          .join('')}</${firstListLine.tag}>`,
      );
      continue;
    }
    const prose: string[] = [];
    while (index < lines.length && listLine(lines[index] ?? '') == null) {
      prose.push(lines[index] ?? '');
      index += 1;
    }
    const joined = prose.map(escapeHtmlChars).join('<br />');
    if (joined.length > 0) {
      parts.push(`<p>${joined}</p>`);
    }
  }
  return parts.join('');
}

/**
 * Convert a plain-text example to HTML for a rich TextArea field.
 *
 * Double newlines become separate blocks. Single newlines soft-break inside a
 * paragraph. Runs of `• ` / `- ` / `* ` become `<ul>` and runs of numbered
 * markers become `<ol>`, so inserted lists match toolbar-made list semantics
 * and wrapped text hangs under the first line.
 */
export function plainTextExampleToRichHtml(text: string): string {
  const normalized = withNormalizedNewlines(text.trim());
  if (!normalized) {
    return '';
  }
  return normalized
    .split(/\n{2,}/)
    .map(blockToRichHtml)
    .filter(bit => bit.length > 0)
    .join('');
}

/** Same projection the character counter and Zod ceiling use (`convertToPlainText`). */
function plainTextLengthOfHtml(html: string): number {
  return convertToPlainText(html).length;
}

const HTML_ENTITY = /&(?:#(\d+)|#x([0-9a-f]+)|([a-z][a-z0-9]+));/gi;
const NAMED_ENTITY_TEXT: Readonly<Record<string, string>> = {
  amp: '&',
  apos: "'",
  bull: '•',
  gt: '>',
  lt: '<',
  nbsp: ' ',
  quot: '"',
  rsquo: '’',
};
const LINE_START_BULLET = /(^|\n)\s*(?:-|•)\s+/g;

function decodeHtmlEntities(value: string): string {
  return value.replace(
    HTML_ENTITY,
    (entity, decimal: string, hexadecimal: string, named: string) => {
      const codePoint = decimal
        ? Number.parseInt(decimal, 10)
        : hexadecimal
        ? Number.parseInt(hexadecimal, 16)
        : undefined;
      if (codePoint !== undefined) {
        try {
          return String.fromCodePoint(codePoint);
        } catch {
          return entity;
        }
      }
      return NAMED_ENTITY_TEXT[named.toLowerCase()] ?? entity;
    },
  );
}

/**
 * Plain text of rich HTML normalized only for editor rewrites that preserve
 * visible content: apostrophe encodings, paragraph/list bullet promotion, and
 * whitespace around rewritten blocks. Native 10tap can perform all three.
 */
function richHtmlMatchText(html: string): string {
  return withNormalizedNewlines(
    decodeHtmlEntities(convertToPlainText(withNormalizedNewlines(html))),
  )
    .replace(/\u2019/g, "'")
    .replace(LINE_START_BULLET, '$1• ')
    .split('\n')
    .map(line => line.replace(/[^\S\n]+/g, ' ').trim())
    .filter(Boolean)
    .join('\n');
}

/** One blank line between the previous end and a new example (rich equivalent of `\n\n`). */
const RICH_HTML_INSERT_GAP = '<p>\u00a0</p>';

function lastBlockOpeningIndex(lower: string, tag: string): number {
  const prefix = `<${tag}`;
  let found = -1;
  let from = 0;
  while (from < lower.length) {
    const index = lower.indexOf(prefix, from);
    if (index < 0) {
      break;
    }
    const next = lower.charAt(index + prefix.length);
    if (next === '' || next === '>' || next === '/' || /\s/.test(next)) {
      found = index;
    }
    from = index + 1;
  }
  return found;
}

function endsWithEmptyBlock(html: string): boolean {
  const trimmed = html.trimEnd();
  const lower = trimmed.toLowerCase();
  for (const tag of ['p', 'div'] as const) {
    const closing = `</${tag}>`;
    if (!lower.endsWith(closing)) {
      continue;
    }
    const opening = lastBlockOpeningIndex(lower, tag);
    const openingEnd = opening < 0 ? -1 : lower.indexOf('>', opening);
    if (opening < 0 || openingEnd < 0) {
      continue;
    }
    const inner = trimmed.slice(openingEnd + 1, -closing.length);
    return inner.replace(/&nbsp;|&#160;|<br\s*\/?>/gi, '').trim().length === 0;
  }
  return false;
}

/** Tags that carry content the plain-text projection cannot show. */
const TEXTLESS_CONTENT_TAG = /<(?:img|hr|table|a\s[^>]*href)\b/i;

/** `''`, `<p></p>`, `<p><br></p>`, `&nbsp;` — nothing to separate the example from. */
function isBlankRichHtml(html: string): boolean {
  if (TEXTLESS_CONTENT_TAG.test(html)) {
    return false;
  }
  return richHtmlMatchText(html).length === 0;
}

/**
 * Like {@link appendExampleText} for rich HTML fields (e.g. `oppProjectDescription`).
 * Converts the example to `<p>` / `<br />` HTML, then appends to the stored value
 * after one empty paragraph. A blank field takes the example alone — no leading gap.
 * `maxLength` is checked against projected plain-text length.
 */
export function appendExampleTextRichHtml(
  currentHtml: string,
  examplePlainText: string,
  maxLength: number,
): { value: string; inserted: boolean } {
  const exampleHtml = plainTextExampleToRichHtml(examplePlainText);
  if (!exampleHtml) {
    return { value: currentHtml, inserted: false };
  }
  const base = currentHtml.trimEnd();
  const separator = endsWithEmptyBlock(base) ? '' : RICH_HTML_INSERT_GAP;
  const combined = isBlankRichHtml(base)
    ? exampleHtml
    : `${base}${separator}${exampleHtml}`;
  if (plainTextLengthOfHtml(combined) > maxLength) {
    return { value: currentHtml, inserted: false };
  }
  return { value: combined, inserted: true };
}

export function exampleAlreadyInserted(
  current: string,
  example: string,
): boolean {
  return withNormalizedNewlines(current).includes(
    withNormalizedNewlines(example),
  );
}

/**
 * Rich-HTML variant of {@link exampleAlreadyInserted}.
 * Compares the example's projected text against the field's projected text, so
 * an example the editor has re-tagged still counts as inserted.
 */
export function exampleAlreadyInsertedRichHtml(
  currentHtml: string,
  examplePlainText: string,
): boolean {
  const needle = richHtmlMatchText(
    plainTextExampleToRichHtml(examplePlainText),
  );
  if (!needle) {
    return false;
  }
  return richHtmlMatchText(currentHtml).includes(needle);
}

export function appendExampleText(
  current: string,
  example: string,
  maxLength: number,
): { value: string; inserted: boolean } {
  const base = current.trimEnd();
  if (base.length === 0) {
    if (example.length > maxLength) {
      return { value: current, inserted: false };
    }
    return { value: example, inserted: true };
  }
  const addition = `\n\n${example}`;
  if (base.length + addition.length > maxLength) {
    return { value: current, inserted: false };
  }
  return { value: base + addition, inserted: true };
}

/** rAF when present, otherwise the next timer. Same fallback both insert consumers use. */
export function scheduleDeferred(run: () => void): void {
  if (typeof requestAnimationFrame === 'function') {
    requestAnimationFrame(run);
    return;
  }
  setTimeout(run, 0);
}

/** Overlay gates for Neutral Insert — same pair Project Description and Deliverables pass. */
export function exampleInsertAvailability(
  current: string,
  maxLength: number,
): {
  canInsertExample: (text: string) => boolean;
  alreadyInserted: (text: string) => boolean;
} {
  return {
    canInsertExample: text =>
      appendExampleText(current, text, maxLength).inserted,
    alreadyInserted: text => exampleAlreadyInserted(current, text),
  };
}
